export const MNIST_IMAGE_SIZE = 28;
export const MNIST_CONTENT_SIZE = 20;
export const MNIST_PIXEL_MAX = 255;

function assertBitmapDimensions(
  bitmap: ArrayLike<number>,
  width: number,
  height: number
): void {
  if (width <= 0 || height <= 0 || bitmap.length !== width * height) {
    throw new Error("Bitmap dimensions do not match its pixel data");
  }
}

export function normalizeAlphaBitmap(
  alphaBitmap: ArrayLike<number>
): Float64Array {
  const normalized = new Float64Array(alphaBitmap.length);

  for (let index = 0; index < alphaBitmap.length; index += 1) {
    const alpha = alphaBitmap[index];
    const clampedAlpha = Number.isFinite(alpha)
      ? Math.max(0, Math.min(MNIST_PIXEL_MAX, alpha))
      : 0;
    normalized[index] = clampedAlpha / MNIST_PIXEL_MAX;
  }

  return normalized;
}

/** Resize grayscale data using area coverage, retaining antialiasing. */
export function resizeGrayscaleBitmap(
  source: ArrayLike<number>,
  sourceWidth: number,
  sourceHeight: number,
  targetWidth: number,
  targetHeight: number
): Float64Array {
  assertBitmapDimensions(source, sourceWidth, sourceHeight);

  if (targetWidth <= 0 || targetHeight <= 0) {
    throw new Error("Target bitmap dimensions must be positive");
  }

  const destination = new Float64Array(targetWidth * targetHeight);
  const scaleX = sourceWidth / targetWidth;
  const scaleY = sourceHeight / targetHeight;

  for (let targetY = 0; targetY < targetHeight; targetY += 1) {
    const sourceYStart = targetY * scaleY;
    const sourceYEnd = (targetY + 1) * scaleY;
    const sourceYStartIndex = Math.floor(sourceYStart);
    const sourceYEndIndex = Math.min(
      sourceHeight - 1,
      Math.ceil(sourceYEnd) - 1
    );

    for (let targetX = 0; targetX < targetWidth; targetX += 1) {
      const sourceXStart = targetX * scaleX;
      const sourceXEnd = (targetX + 1) * scaleX;
      const sourceXStartIndex = Math.floor(sourceXStart);
      const sourceXEndIndex = Math.min(
        sourceWidth - 1,
        Math.ceil(sourceXEnd) - 1
      );
      let weightedValue = 0;

      for (let sourceY = sourceYStartIndex; sourceY <= sourceYEndIndex; sourceY += 1) {
        const yOverlap = Math.min(sourceYEnd, sourceY + 1) -
          Math.max(sourceYStart, sourceY);

        for (let sourceX = sourceXStartIndex; sourceX <= sourceXEndIndex; sourceX += 1) {
          const xOverlap = Math.min(sourceXEnd, sourceX + 1) -
            Math.max(sourceXStart, sourceX);

          if (xOverlap > 0 && yOverlap > 0) {
            weightedValue += source[sourceY * sourceWidth + sourceX] *
              xOverlap * yOverlap;
          }
        }
      }

      destination[targetY * targetWidth + targetX] =
        weightedValue / (scaleX * scaleY);
    }
  }

  return destination;
}

function translateBitmap(
  bitmap: ArrayLike<number>,
  width: number,
  height: number,
  offsetX: number,
  offsetY: number
): Float64Array {
  assertBitmapDimensions(bitmap, width, height);

  const translated = new Float64Array(bitmap.length);

  for (let sourceY = 0; sourceY < height; sourceY += 1) {
    for (let sourceX = 0; sourceX < width; sourceX += 1) {
      const value = bitmap[sourceY * width + sourceX];
      if (value === 0) continue;

      const targetX = sourceX + offsetX;
      const targetY = sourceY + offsetY;
      const targetX0 = Math.floor(targetX);
      const targetY0 = Math.floor(targetY);
      const fractionX = targetX - targetX0;
      const fractionY = targetY - targetY0;

      const addValue = (x: number, y: number, weight: number): void => {
        if (weight <= 0 || x < 0 || x >= width || y < 0 || y >= height) {
          return;
        }

        translated[y * width + x] += value * weight;
      };

      addValue(targetX0, targetY0, (1 - fractionX) * (1 - fractionY));
      addValue(targetX0 + 1, targetY0, fractionX * (1 - fractionY));
      addValue(targetX0, targetY0 + 1, (1 - fractionX) * fractionY);
      addValue(targetX0 + 1, targetY0 + 1, fractionX * fractionY);
    }
  }

  return translated;
}

export function centerBitmapByMass(
  bitmap: ArrayLike<number>,
  width = MNIST_IMAGE_SIZE,
  height = MNIST_IMAGE_SIZE
): Float64Array {
  assertBitmapDimensions(bitmap, width, height);

  let mass = 0;
  let weightedX = 0;
  let weightedY = 0;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const value = bitmap[y * width + x];
      mass += value;
      weightedX += x * value;
      weightedY += y * value;
    }
  }

  if (mass === 0) {
    return new Float64Array(width * height);
  }

  const centerX = weightedX / mass;
  const centerY = weightedY / mass;
  const targetCenter = (width - 1) / 2;

  return translateBitmap(
    bitmap,
    width,
    height,
    targetCenter - centerX,
    targetCenter - centerY
  );
}

export function preprocessAlphaBitmap(
  alphaBitmap: ArrayLike<number>,
  sourceWidth: number,
  sourceHeight: number
): Float64Array {
  assertBitmapDimensions(alphaBitmap, sourceWidth, sourceHeight);

  const normalized = normalizeAlphaBitmap(alphaBitmap);
  let minX = sourceWidth;
  let maxX = -1;
  let minY = sourceHeight;
  let maxY = -1;

  for (let y = 0; y < sourceHeight; y += 1) {
    for (let x = 0; x < sourceWidth; x += 1) {
      if (normalized[y * sourceWidth + x] > 0) {
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y);
      }
    }
  }

  if (maxX < minX || maxY < minY) {
    return new Float64Array(MNIST_IMAGE_SIZE * MNIST_IMAGE_SIZE);
  }

  const contentWidth = maxX - minX + 1;
  const contentHeight = maxY - minY + 1;
  const scale = Math.min(
    MNIST_CONTENT_SIZE / contentWidth,
    MNIST_CONTENT_SIZE / contentHeight
  );
  const resizedWidth = Math.max(1, Math.round(contentWidth * scale));
  const resizedHeight = Math.max(1, Math.round(contentHeight * scale));
  const cropped = new Float64Array(contentWidth * contentHeight);

  for (let y = 0; y < contentHeight; y += 1) {
    for (let x = 0; x < contentWidth; x += 1) {
      cropped[y * contentWidth + x] =
        normalized[(minY + y) * sourceWidth + minX + x];
    }
  }

  const resized = resizeGrayscaleBitmap(
    cropped,
    contentWidth,
    contentHeight,
    resizedWidth,
    resizedHeight
  );
  const centered = new Float64Array(MNIST_IMAGE_SIZE * MNIST_IMAGE_SIZE);
  const offsetX = Math.floor((MNIST_IMAGE_SIZE - resizedWidth) / 2);
  const offsetY = Math.floor((MNIST_IMAGE_SIZE - resizedHeight) / 2);

  for (let y = 0; y < resizedHeight; y += 1) {
    for (let x = 0; x < resizedWidth; x += 1) {
      centered[(offsetY + y) * MNIST_IMAGE_SIZE + offsetX + x] =
        resized[y * resizedWidth + x];
    }
  }

  return centerBitmapByMass(centered);
}
